import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TruckIcon, ZapIcon, ScaleIcon, FlaskConicalIcon, CheckCircle2Icon, PrinterIcon, EditIcon } from "lucide-react";
import BulkReceiptEditModal from "@/components/BulkReceiptEditModal";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLOR = {
  AGUARDANDO_LAUDO: "bg-[#FF9F0A]/20 text-[#FF9F0A] border-[#FF9F0A]/40",
  PRONTO_DESCARGA: "bg-[#00E5FF]/20 text-[#00E5FF] border-[#00E5FF]/40",
  EM_ENVASE: "bg-[#FFD60A]/20 text-[#FFD60A] border-[#FFD60A]/40",
  CONCLUIDO: "bg-[#34C759]/20 text-[#34C759] border-[#34C759]/40",
};

export default function Recebimento() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "manager";
  const [products, setProducts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({
    nf_recebimento: "",
    remessa: "",
    fornecedor: "",
    lote_fabricante: "",
    codigo_produto: "",
    peso_bruto: "",
    densidade: "",
  });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/products"), api.get("/bulk-receipts")]);
    setProducts(p.data);
    setReceipts(r.data);
  };
  useEffect(() => { load(); }, []);

  const selectedProduct = products.find((p) => String(p.codigo) === String(form.codigo_produto));

  const onProductChange = (codigo) => {
    const prod = products.find((p) => String(p.codigo) === String(codigo));
    setForm((f) => ({ ...f, codigo_produto: codigo, densidade: prod ? String(prod.densidade) : "" }));
  };

  const volume = form.peso_bruto && form.densidade
    ? (parseFloat(form.peso_bruto) / parseFloat(form.densidade)).toFixed(2)
    : "—";

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return toast.error("Selecione o produto");
    setLoading(true);
    try {
      await api.post("/bulk-receipts", {
        nf_recebimento: form.nf_recebimento,
        remessa: form.remessa,
        fornecedor: form.fornecedor,
        lote_fabricante: form.lote_fabricante,
        codigo_produto: parseInt(form.codigo_produto, 10),
        nome_produto: selectedProduct.nome,
        funcao_quimica: selectedProduct.funcao_quimica,
        densidade: parseFloat(form.densidade),
        peso_bruto: parseFloat(form.peso_bruto),
        usuario_recebimento: "operator",
      });
      toast.success("Caminhão registrado — aguardando laudo");
      setForm({ nf_recebimento: "", remessa: "", fornecedor: "", lote_fabricante: "", codigo_produto: "", peso_bruto: "", densidade: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const liberar = async (id) => {
    try {
      await api.post(`/bulk-receipts/${id}/liberar`);
      toast.success("Liberado para descarga");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Tela 01 · Operacional</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Recebimento de Granel</h1>
          <p className="text-sm text-muted-foreground mt-2">Registro de chegada de caminhão-tanque. O sistema calcula automaticamente o volume teórico (Peso ÷ Densidade).</p>
        </div>
        <TruckIcon className="w-12 h-12 text-[#FFD60A]" strokeWidth={1.2} />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <Card className="lg:col-span-3 bg-[#14151A] border-border rounded-none p-6">
          <h2 className="font-display text-2xl font-bold mb-4">Nova entrada</h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="NF Recebimento" id="nf">
              <Input data-testid="rec-nf" value={form.nf_recebimento} onChange={(e) => setForm({ ...form, nf_recebimento: e.target.value })} required className="rounded-none bg-[#0A0A0C] font-mono h-11" placeholder="123456" />
            </Field>
            <Field label="Remessa (ID da Viagem)" id="rem">
              <Input data-testid="rec-remessa" value={form.remessa} onChange={(e) => setForm({ ...form, remessa: e.target.value })} required className="rounded-none bg-[#0A0A0C] font-mono h-11" placeholder="REM-2026-001" />
            </Field>
            <Field label="Fornecedor" id="for">
              <Input data-testid="rec-fornecedor" value={form.fornecedor} onChange={(e) => setForm({ ...form, fornecedor: e.target.value })} required className="rounded-none bg-[#0A0A0C] h-11" placeholder="Indústria Química SA" />
            </Field>
            <Field label="Lote Fabricante" id="lot">
              <Input data-testid="rec-lote" value={form.lote_fabricante} onChange={(e) => setForm({ ...form, lote_fabricante: e.target.value })} required className="rounded-none bg-[#0A0A0C] font-mono h-11" placeholder="LF-2026-001" />
            </Field>
            <Field label="Produto" id="prod" full>
              <Select value={form.codigo_produto} onValueChange={onProductChange}>
                <SelectTrigger data-testid="rec-produto" className="rounded-none bg-[#0A0A0C] h-11 border-border">
                  <SelectValue placeholder="Selecione o produto químico" />
                </SelectTrigger>
                <SelectContent className="bg-[#14151A] border-border rounded-none">
                  {products.map((p) => (
                    <SelectItem key={p.id} value={String(p.codigo)} data-testid={`rec-prod-${p.codigo}`}>
                      <span className="font-mono text-xs text-[#FFD60A] mr-2">{p.codigo}</span>
                      {p.nome} <span className="text-muted-foreground ml-1">· {p.funcao_quimica}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Densidade (kg/L)" id="dens">
              <Input data-testid="rec-densidade" type="number" step="0.001" value={form.densidade} onChange={(e) => setForm({ ...form, densidade: e.target.value })} required className="rounded-none bg-[#0A0A0C] font-mono h-11" placeholder="1.071" />
            </Field>
            <Field label="Peso Bruto Inicial (kg)" id="peso">
              <Input data-testid="rec-peso" type="number" step="0.01" value={form.peso_bruto} onChange={(e) => setForm({ ...form, peso_bruto: e.target.value })} required className="rounded-none bg-[#0A0A0C] font-mono h-11" placeholder="26500" />
            </Field>

            <div className="md:col-span-2 flex justify-end pt-2">
              <Button type="submit" disabled={loading} data-testid="rec-submit" className="bg-[#FFD60A] text-black hover:bg-[#ffdf3a] rounded-none h-12 px-8 font-bold uppercase tracking-wider">
                <ZapIcon className="w-4 h-4 mr-2" />
                {loading ? "Registrando..." : "Registrar Caminhão"}
              </Button>
            </div>
          </form>
        </Card>

        {/* Live calc panel */}
        <Card className="lg:col-span-2 bg-[#0A0A0C] border-border border-l-2 border-l-[#FFD60A] rounded-none p-6 relative overflow-hidden">
          <div className="scanline absolute inset-0 pointer-events-none" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-2">Cálculo em tempo real</div>
          <h3 className="font-display text-2xl font-bold mb-6">Conversão Peso → Volume</h3>

          <div className="space-y-5">
            <Metric icon={ScaleIcon} label="Peso Bruto" value={form.peso_bruto || "—"} unit="kg" />
            <Metric icon={FlaskConicalIcon} label="Densidade" value={form.densidade || "—"} unit="kg/L" />
            <div className="border-t border-border pt-5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Volume Teórico</div>
              <div className="kpi-num text-6xl text-[#FFD60A]" data-testid="rec-volume">{volume}</div>
              <div className="text-sm text-muted-foreground mt-1">litros disponíveis para envase</div>
            </div>
            {selectedProduct && (
              <div className="border border-border p-3 bg-[#14151A]">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Produto selecionado</div>
                <div className="font-display text-lg font-bold mt-1">{selectedProduct.nome}</div>
                <div className="text-xs text-[#00E5FF] font-mono mt-1">{selectedProduct.funcao_quimica}</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* History */}
      <Card className="mt-8 bg-[#14151A] border-border rounded-none p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold">Caminhões registrados</h2>
          <div className="text-xs text-muted-foreground font-mono">{receipts.length} registros</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0C] text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Remessa</th>
                <th className="text-left px-4 py-3">NF</th>
                <th className="text-left px-4 py-3">Produto</th>
                <th className="text-right px-4 py-3">Peso (kg)</th>
                <th className="text-right px-4 py-3">Vol. Teórico</th>
                <th className="text-right px-4 py-3">Envasado</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Ação</th>
              </tr>
            </thead>
            <tbody>
              {receipts.length === 0 && (
                <tr><td colSpan="8" className="text-center text-muted-foreground py-8">Nenhum registro ainda.</td></tr>
              )}
              {receipts.map((r) => (
                <tr key={r.id} className="border-t border-border/60 hover:bg-[#1C1D24]" data-testid={`rec-row-${r.remessa}`}>
                  <td className="px-4 py-3 font-mono text-[#FFD60A]">{r.remessa}</td>
                  <td className="px-4 py-3 font-mono">{r.nf_recebimento}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.funcao_quimica}</div>
                    <div className="text-xs text-muted-foreground font-mono">Cód. {r.codigo_produto}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{r.peso_bruto}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#00E5FF]">{r.volume_calculado}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.volume_envasado || 0}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-none border ${STATUS_COLOR[r.status] || "border-border"}`}>{r.status.replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status === "AGUARDANDO_LAUDO" && (
                      <Button size="sm" onClick={() => liberar(r.id)} data-testid={`liberar-${r.remessa}`} className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80 h-8 mr-2">
                        <CheckCircle2Icon className="w-3 h-3 mr-1" /> Liberar laudo
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditTarget(r)}
                        data-testid={`editar-rec-${r.remessa}`}
                        className="rounded-none border-[#FFD60A]/60 text-[#FFD60A] hover:bg-[#FFD60A]/10 h-8 mr-2"
                        title="Editar dados do recebimento"
                      >
                        <EditIcon className="w-3 h-3 mr-1" /> Editar
                      </Button>
                    )}
                    <a
                      href={`/romaneio/recebimento/${encodeURIComponent(r.remessa)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`imprimir-rec-${r.remessa}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#FFD60A] hover:underline"
                    >
                      <PrinterIcon className="w-3 h-3" /> Romaneio
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <BulkReceiptEditModal
        open={!!editTarget}
        receipt={editTarget}
        products={products}
        onClose={() => setEditTarget(null)}
        onSaved={() => load()}
      />
    </div>
  );
}

function Field({ label, id, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label htmlFor={id} className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Metric({ icon: Icon, label, value, unit }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-3">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-muted-foreground" />
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      </div>
      <div className="font-mono text-lg">
        <span className="text-white">{value}</span>
        <span className="text-muted-foreground text-xs ml-1">{unit}</span>
      </div>
    </div>
  );
}
